import Anthropic from "@anthropic-ai/sdk";
import { ChatCompletionRequestMessage } from "openai";
import { ProviderSettings, Settings } from "../db";
import { Provider, ProviderModel, providerRegistry } from "../utils/providers";

// Define Anthropic models (initial models until we fetch from API)
const anthropicModels: ProviderModel[] = [
  {
    id: "claude-3-opus-20240229",
    name: "Claude 3 Opus",
    description: "Most powerful Claude model for complex tasks",
    contextWindow: 200000,
    maxTokens: 4096,
  },
  {
    id: "claude-3-sonnet-20240229",
    name: "Claude 3 Sonnet",
    description: "Balanced Claude model for most tasks",
    contextWindow: 200000,
    maxTokens: 4096,
  },
  {
    id: "claude-3-haiku-20240307",
    name: "Claude 3 Haiku",
    description: "Fastest and most compact Claude model",
    contextWindow: 200000,
    maxTokens: 4096,
  },
];

// Helper function to get provider settings from the main settings object
function getProviderSettings(settings: Settings): ProviderSettings {
  const providerSettings = settings.providerSettings?.[settings.provider || 'anthropic'];
  if (!providerSettings) {
    throw new Error("Provider settings not found");
  }
  return providerSettings;
}

// Helper function to get Anthropic client
function getAnthropicClient(settings: Settings): Anthropic {
  const providerSettings = getProviderSettings(settings);
  
  if (!providerSettings.apiKey) {
    throw new Error("API Key is required");
  }
  
  return new Anthropic({
    apiKey: providerSettings.apiKey,
    dangerouslyAllowBrowser: true,
  });
}

// Helper function to map OpenAI messages to Anthropic format
function mapMessagesToAnthropic(
  messages: ChatCompletionRequestMessage[]
): { system?: string; messages: Anthropic.Messages.MessageParam[] } {
  let systemMessage: string | undefined = undefined;
  const anthropicMessages: Anthropic.Messages.MessageParam[] = [];

  messages.forEach((msg) => {
    if (msg.role === "system") {
      systemMessage = msg.content;
    } else if (msg.role === "user" || msg.role === "assistant") {
      if (msg.content) {
        anthropicMessages.push({ role: msg.role, content: msg.content });
      }
    }
  });

  return { system: systemMessage, messages: anthropicMessages };
}

// Create the Anthropic provider
const anthropicProvider: Provider = {
  id: "anthropic",
  name: "Anthropic",
  description: "Claude AI models by Anthropic",
  authType: "api-key",
  apiKeyUrl: "https://console.anthropic.com/settings/keys",
  defaultModel: "claude-3-haiku-20240307",
  models: anthropicModels,

  // Fetch models from Anthropic API using the SDK
  async fetchModels(settings: Settings): Promise<ProviderModel[]> {
    try {
      const client = getAnthropicClient(settings);

      // Anthropic's Node.js SDK provides a listModels method
      const response = await client.models.list();
      
      // Convert Anthropic models to our format - handle type issues with explicit mapping
      const models: ProviderModel[] = [];
      
      // Process each model from the response
      for (const model of response.data) {
        // Format model name nicely from the ID
        const formattedName = model.id
          .replace('claude-', 'Claude ')
          .split('-')
          .map(word => word === 'v' ? 'v' : word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
          
        // Add to our models array
        models.push({
          id: model.id,
          name: formattedName,
          description: `Anthropic ${model.id} model`,
          contextWindow: 200000, // Default value since Anthropic SDK doesn't expose this
          maxTokens: 4096, // Default value since Anthropic SDK doesn't expose this
        });
      }
      
      console.log(`Fetched ${models.length} Anthropic models using SDK`);
      return models;
    } catch (error: any) {
      console.error("Error fetching Anthropic models:", error);
      // Return default models if API call fails
      return anthropicModels;
    }
  },

  // Validate credentials
  async validateCredentials(settings: Settings): Promise<boolean> {
    try {
      const providerSettings = getProviderSettings(settings);
      
      if (!providerSettings.apiKey) {
        throw new Error("API Key is required");
      }

      const client = getAnthropicClient(settings);
      const validationModel = "claude-3-haiku-20240307"; // Use a fast model for validation

      await client.messages.create({
        model: validationModel,
        max_tokens: 1,
        messages: [{ role: "user", content: "Hi" }],
      });

      return true;
    } catch (error: any) {
      console.error("Anthropic validation error:", error);
      const message = error.error?.message || error.message;
      throw new Error(`Anthropic API Key validation failed: ${message}`);
    }
  },

  // Create chat completion
  async createChatCompletion(
    settings: Settings,
    messages: ChatCompletionRequestMessage[]
  ): Promise<any> {
    try {
      const providerSettings = getProviderSettings(settings);
      
      if (!providerSettings.apiKey) {
        throw new Error("API Key is required");
      }

      if (!settings.model) {
        throw new Error("Model is not configured");
      }

      const client = getAnthropicClient(settings);
      const { system, messages: anthropicMessages } = mapMessagesToAnthropic(messages);
      const maxTokens = 4096; // Default value

      if (anthropicMessages.length === 0) {
        throw new Error("No valid user or assistant messages found for Anthropic API call");
      }

      const response = await client.messages.create({
        model: settings.model,
        max_tokens: maxTokens,
        ...(system && { system }),
        messages: anthropicMessages,
      });

      // Convert Anthropic response to OpenAI-like format for compatibility
      const assistantContent = response.content
        .filter((block) => block.type === "text")
        .map((block) => (block as Anthropic.Messages.TextBlock).text)
        .join("");

      return {
        id: response.id,
        object: "chat.completion",
        created: Math.floor(Date.now() / 1000),
        model: response.model,
        choices: [
          {
            index: 0,
            message: {
              role: "assistant",
              content: assistantContent,
            },
            finish_reason:
              response.stop_reason === "max_tokens"
                ? "length"
                : response.stop_reason === "stop_sequence"
                ? "stop"
                : "stop",
          },
        ],
        usage: {
          prompt_tokens: response.usage.input_tokens,
          completion_tokens: response.usage.output_tokens,
          total_tokens: response.usage.input_tokens + response.usage.output_tokens,
        },
      };
    } catch (error: any) {
      console.error("Anthropic chat completion error:", error);
      const message = error.error?.message || error.message;
      throw new Error(`Anthropic chat completion failed: ${message}`);
    }
  },

  // Create streaming chat completion
  async createStreamChatCompletion(
    settings: Settings,
    messages: ChatCompletionRequestMessage[],
    chatId: string,
    messageId: string
  ): Promise<any> {
    try {
      // We need to import db here to avoid circular dependency
      const { db } = await import("../db");

      const providerSettings = getProviderSettings(settings);
      
      if (!providerSettings.apiKey) {
        throw new Error("API Key is required");
      }

      if (!settings.model) {
        throw new Error("Model is not configured");
      }

      const client = getAnthropicClient(settings);
      const { system, messages: anthropicMessages } = mapMessagesToAnthropic(messages);
      const maxTokens = 4096; // Default value

      if (anthropicMessages.length === 0) {
        throw new Error("No valid user or assistant messages found for Anthropic API call");
      }

      // Anthropic doesn't have a direct streaming API like OpenAI's
      // So we'll simulate streaming by updating the message content as we receive it
      db.messages.update(messageId, { content: "Generating response..." });

      const response = await client.messages.create({
        model: settings.model,
        max_tokens: maxTokens,
        ...(system && { system }),
        messages: anthropicMessages,
      });

      const assistantContent = response.content
        .filter((block) => block.type === "text")
        .map((block) => (block as Anthropic.Messages.TextBlock).text)
        .join("");

      // Update the message with the complete response
      db.messages.update(messageId, { content: assistantContent });

      // Update token count
      db.chats.where({ id: chatId }).modify((chat: any) => {
        const totalTokens = response.usage.input_tokens + response.usage.output_tokens;
        if (chat.totalTokens) {
          chat.totalTokens += totalTokens;
        } else {
          chat.totalTokens = totalTokens;
        }
      });

      return response;
    } catch (error: any) {
      console.error("Anthropic streaming error:", error);
      const message = error.error?.message || error.message;
      
      // We need to import db here to avoid circular dependency
      const { db } = await import("../db");
      db.messages.update(messageId, { content: `Error: ${message}` });
      
      throw new Error(`Anthropic streaming failed: ${message}`);
    }
  },
};

// Register the Anthropic provider
providerRegistry.register(anthropicProvider);

export default anthropicProvider;
